/**
 * Wire types for the `payments` module — derived field-by-field from
 * `serializeInvoice` / `serializeInvoiceRow` / `serializeInvoiceAsPayment` /
 * `serializePayment` / `serializePaymentRow` / `serializeLedgerEntry`
 * (refactor/backend/src/modules/payments/serializers.ts). snake_case
 * verbatim, no camelCase mapping layer — see the FIELD-NAMING DECISION note
 * in src/types/user.ts.
 *
 * MONEY IS A STRING. Every decimal field below (`balance`, `amount_due`,
 * `amount_paid`, `amount`) is a quantised string like `"1000.00"`, never a
 * JSON number — never do arithmetic on these beyond display formatting.
 */

/** Django `payments.models.ChargeType`. */
export const CHARGE_TYPES = [
  'RENT',
  'WATER',
  'GARBAGE',
  'ELECTRICITY',
  'INTERNET',
  'DEPOSIT',
  'LATE_FEE',
  'OTHER',
] as const;
export type ChargeType = (typeof CHARGE_TYPES)[number];

/** Django `payments.models.InvoiceStatus`. */
export const INVOICE_STATUSES = ['DRAFT', 'OPEN', 'PAID', 'PARTIAL', 'VOID', 'OVERDUE'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** Django `payments.models.PaymentStatus`. */
export const PAYMENT_STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'REFUNDED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** Django `payments.models.PaymentMethod`. */
export const PAYMENT_METHODS = ['CASH', 'BANK_TRANSFER', 'CARD', 'MOBILE_MONEY', 'CHECK'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** Django `payments.models.LedgerEntryType`. */
export const LEDGER_ENTRY_TYPES = ['DEBIT', 'CREDIT'] as const;
export type LedgerEntryType = (typeof LEDGER_ENTRY_TYPES)[number];

/**
 * `InvoiceSerializer` — serializers.ts:71-91. `fields = '__all__'` plus the
 * computed `balance` (`amount_due - amount_paid`, quantised to 2dp).
 */
export interface Invoice {
  id: number;
  tenant_name: string;
  unit_number: string;
  property_name: string;
  balance: string;
  invoice_number: string;
  charge_type: ChargeType;
  amount_due: string;
  amount_paid: string;
  due_date: string;
  billing_period: string;
  status: InvoiceStatus;
  notes: string;
  created_at: string;
  updated_at: string;
  lease: number;
  unit: number;
}

/** `InvoiceListSerializer` — serializers.ts:94-107. */
export interface InvoiceListRow {
  id: number;
  invoice_number: string;
  tenant_name: string;
  unit_number: string;
  property_name: string;
  charge_type: ChargeType;
  amount_due: string;
  amount_paid: string;
  due_date: string;
  status: InvoiceStatus;
}

/**
 * `InvoiceAsPaymentSerializer` — serializers.ts:119-137. `id` is the INVOICE
 * id (fed straight back to the M-Pesa endpoints as `invoice_id`); `amount` is
 * the outstanding BALANCE, not `amount_due`. Returned as a bare array by
 * `payments/upcoming/` and `payments/overdue/`, never the `Paginated<T>`
 * envelope.
 */
export interface InvoiceAsPayment {
  id: number;
  lease: number;
  tenant_name: string;
  unit_number: string;
  property_name: string;
  amount: string;
  due_date: string;
  status: InvoiceStatus;
  charge_type: ChargeType;
  billing_period: string;
  is_overdue: boolean;
  created_at: string;
}

/** `PaymentSerializer` — serializers.ts:144-157. `fields = '__all__'`. */
export interface Payment {
  id: number;
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod;
  transaction_reference: string;
  status: PaymentStatus;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  invoice: number | null;
}

/**
 * `PaymentListSerializer` — serializers.ts:168-178. `invoice_number` is
 * `null` for a payment with no invoice (see the DIVERGENCE note there).
 */
export interface PaymentListRow {
  id: number;
  invoice_number: string | null;
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod;
  transaction_reference: string;
  status: PaymentStatus;
}

/** `LedgerEntrySerializer` — serializers.ts:181-192. `fields = '__all__'`. */
export interface LedgerEntry {
  id: number;
  entry_type: LedgerEntryType;
  amount: string;
  description: string;
  timestamp: string;
  tenant: number;
  invoice: number | null;
  payment: number | null;
}

// ---------------------------------------------------------------------------
// Write requests
// ---------------------------------------------------------------------------

/**
 * `invoiceCreateSchema` on write (serializers.ts:205-214). Plain
 * `POST payments/invoices/` writes ONLY the invoice row — it does NOT create
 * a ledger entry. Only `record_actual_payment` (below) touches the ledger.
 */
export interface InvoiceCreateRequest {
  lease: number;
  unit: number;
  charge_type?: ChargeType | undefined;
  amount_due: number | string;
  due_date: string;
  billing_period: string;
  status?: InvoiceStatus | undefined;
  notes?: string | undefined;
}

/** `invoicePatchSchema` — every field of `InvoiceCreateRequest`, optional. */
export type InvoicePatchRequest = Partial<InvoiceCreateRequest>;

/**
 * `paymentCreateSchema` on write (serializers.ts:232-238). This is the plain
 * CRUD payment endpoint — it also does NOT touch the ledger or invoice
 * status. Use `RecordActualPaymentRequest` below to actually settle a
 * balance.
 */
export interface PaymentCreateRequest {
  invoice?: number | null | undefined;
  amount: number | string;
  payment_method: PaymentMethod;
  transaction_reference: string;
  metadata?: Record<string, unknown> | undefined;
}

/** `paymentPatchSchema` — every field of `PaymentCreateRequest`, optional. */
export type PaymentPatchRequest = Partial<PaymentCreateRequest>;

/**
 * `POST payments/payments/{invoice_id}/record_actual_payment/` request body.
 *
 * NOT `PaymentCreateRequest` — this endpoint has no DRF serializer in Django.
 * The view reads `amount`/`method`/`reference` straight off the body with a
 * Django-style FALSY check (`''` and `0` both count as missing), and its
 * error body is `{error: string}`, not field errors. This is the only write
 * that actually settles a balance (via `AccountService.recordPayment`),
 * producing a `Payment` row plus a ledger CREDIT.
 */
export interface RecordActualPaymentRequest {
  amount: number | string;
  method: PaymentMethod;
  reference: string;
}
